import { Category } from "./Category.js";
import { Locale, type LocalizedString } from "./Locale.js";

export enum ProductType {
	VerticalTowerSystems = "VerticalTowerSystems",
	AutonomousRobots = "AutonomousRobots",
	StackerCranes = "StackerCranes",
	ConveyorSystems = "ConveyorSystems",
	ControlSoftware = "ControlSoftware",
}

export const productTypeNameMap: Record<ProductType, LocalizedString> = {
	[ProductType.VerticalTowerSystems]: {
		[Locale.cs]: "Vertikální věžové systémy",
		[Locale.en]: "Vertical tower systems",
	},
	[ProductType.AutonomousRobots]: {
		[Locale.cs]: "Autonomní roboti",
		[Locale.en]: "Autonomous robots",
	},
	[ProductType.StackerCranes]: {
		[Locale.cs]: "Regálové zakladače",
		[Locale.en]: "Stacker cranes",
	},
	[ProductType.ConveyorSystems]: {
		[Locale.cs]: "Dopravníkové systémy",
		[Locale.en]: "Conveyor systems",
	},
	[ProductType.ControlSoftware]: {
		[Locale.cs]: "Řídící software",
		[Locale.en]: "Control software",
	},
};

export const productTypeSlugMap: Record<ProductType, LocalizedString> = {
	[ProductType.VerticalTowerSystems]: {
		[Locale.cs]: "vertikalni-vezove-systemy",
		[Locale.en]: "vertical-tower-systems",
	},
	[ProductType.AutonomousRobots]: {
		[Locale.cs]: "autonomni-roboti",
		[Locale.en]: "autonomous-robots",
	},
	[ProductType.StackerCranes]: {
		[Locale.cs]: "regalove-zakladace",
		[Locale.en]: "stacker-cranes",
	},
	[ProductType.ConveyorSystems]: {
		[Locale.cs]: "dopravnikove-systemy",
		[Locale.en]: "conveyor-systems",
	},
	[ProductType.ControlSoftware]: {
		[Locale.cs]: "ridici-software",
		[Locale.en]: "control-software",
	},
};

export const productTypeShortDescMap: Record<ProductType, LocalizedString> = {
	[ProductType.VerticalTowerSystems]: {
		[Locale.cs]:
			"Vertikální zakladače skladují zboží ve velké výšce bezpečně a zcela pod kontrolou. Technologie zajišťuje optimální využití prostoru s úsporou 70–90 % plochy oproti standardnímu regálu a výrazně zrychluje tok materiálu.",
		[Locale.en]:
			"Vertical storage lifts store goods at great heights safely and completely under control. The technology ensures optimum use of space, saving 70–90% of the area compared to standard shelving, and significantly speeds up material flow.",
	},
	[ProductType.AutonomousRobots]: {
		[Locale.cs]:
			"Robotická řešení Geek+ představují špičkovou technologickou platformu zaměřenou na inteligentní robotiku, která transformuje skladové operace. Pokročilé roboty zvládají vychystávání, třídění i přepravu zboží.",
		[Locale.en]:
			"Geek+ robotic solutions represent a cutting-edge technology platform focused on intelligent robotics that transforms warehouse operations. Advanced robots handle picking, sorting, and transporting goods.",
	},
	[ProductType.StackerCranes]: {
		[Locale.cs]:
			"Plně automatizované regálové zakladače zvyšují kvalitu, flexibilitu a efektivitu skladu. Nabízíme technologie pro automatizované skladování a vychystávání palet i menšího zboží jako kartony nebo přepravky.",
		[Locale.en]:
			"Fully automated stacker cranes increase the quality, flexibility, and efficiency of the warehouse. We offer technologies for automated storage and picking of pallets and smaller goods such as cartons or crates.",
	},
	[ProductType.ConveyorSystems]: {
		[Locale.cs]:
			"Integrované dopravníkové systémy zajišťují plynulý tok materiálu celým skladem. Navrhujeme a dodáváme řešení přesně na míru vašim provozním požadavkům a propojujeme je s dalšími technologiemi.",
		[Locale.en]:
			"Integrated conveyor systems ensure a smooth flow of materials throughout the warehouse. We design and supply solutions precisely tailored to your operational requirements and connect them with other technologies.",
	},
	[ProductType.ControlSoftware]: {
		[Locale.cs]:
			"Inteligentní software VertiNode AI je klíčovým prvkem pro řízení moderních logistických operací. Podporuje rozhodování v reálném čase, zvyšuje spolehlivost procesů a umožňuje flexibilní reakci na změny.",
		[Locale.en]:
			"VertiNode AI intelligent software is a key element in managing modern logistics operations. It supports real-time decision-making, increases process reliability, and enables flexible responses to changes.",
	},
};

export const productTypeLongDescMap: Record<ProductType, LocalizedString> = {
	[ProductType.VerticalTowerSystems]: {
		[Locale.cs]:
			"Automatické věžové sklady (VLM) ukládají zboží do dvou řad výškových boxů a extraktor přiváží požadovanou polici přímo k výdejovému oknu. Na ploše jednoho parkovacího místa získáte stovky aktivních pozic, ergonomický výdej a přesnou evidenci skladu.",
		[Locale.en]:
			"Automated vertical lift modules (VLM) store goods in two rows of tall bays and an extractor delivers the requested tray directly to the operator's opening. In the footprint of a parking space you gain hundreds of active positions, ergonomic picking and precise inventory records.",
	},
	[ProductType.AutonomousRobots]: {
		[Locale.cs]:
			"Autonomní mobilní roboti přinášejí do skladu flexibilitu a vysokou propustnost. Díky navigaci v reálném čase a orchestraci systémem VertiNode přizpůsobí kapacitu okamžité poptávce a snadno se rozšiřují spolu s vaším provozem.",
		[Locale.en]:
			"Autonomous mobile robots bring flexibility and high throughput to the warehouse. With real-time navigation and orchestration by VertiNode, they scale capacity to demand and expand seamlessly with your operation.",
	},
	[ProductType.StackerCranes]: {
		[Locale.cs]:
			"Regálové zakladače a shuttle systémy zajišťují nepřetržitý provoz při skladování palet i drobného zboží. Maximálně využívají výškovou kapacitu skladu a spolupracují s dopravníky, kompletačními stanicemi i řídícím softwarem.",
		[Locale.en]:
			"Stacker cranes and shuttle systems keep pallet and small-goods storage running non-stop. They maximise vertical capacity and integrate with conveyors, picking stations, and control software.",
	},
	[ProductType.ConveyorSystems]: {
		[Locale.cs]:
			"Dopravníkové systémy propojují jednotlivá pracoviště do jednoho materiálového toku. Modulární sestavy dopravníků, třídících tratí a přechodů navrhujeme přesně podle layoutu haly a požadované propustnosti.",
		[Locale.en]:
			"Conveyor systems tie individual workstations into one continuous material flow. Modular runs of conveyors, sorters, and transfer stations are designed to fit the exact layout and throughput of your facility.",
	},
	[ProductType.ControlSoftware]: {
		[Locale.cs]:
			"Řídící software VertiNode AI orchestruje veškerou skladovou techniku a dodává rozhodovacím systémům data v reálném čase. Nabízí modulární architekturu, otevřené API a plnou integraci s ERP a WMS.",
		[Locale.en]:
			"VertiNode AI control software orchestrates every piece of warehouse technology and feeds real-time data to decision systems. It offers a modular architecture, an open API, and full integration with ERP and WMS platforms.",
	},
};

// Only product types that back real products in the DB have a Category mapping.
// Marketing-only tiles (ConveyorSystems, ControlSoftware) intentionally omitted.
export const productTypeCategoryMap: Partial<Record<ProductType, Category>> = {
	[ProductType.VerticalTowerSystems]: Category.Racks,
	[ProductType.AutonomousRobots]: Category.Robots,
	[ProductType.StackerCranes]: Category.Cranes,
};

export const categoryProductTypeMap: Record<Category, ProductType> = {
	[Category.Racks]: ProductType.VerticalTowerSystems,
	[Category.Robots]: ProductType.AutonomousRobots,
	[Category.Cranes]: ProductType.StackerCranes,
};
